import { useState, useEffect, useCallback } from "react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/shadcn";
import * as blockNoteLocales from "@blocknote/core/locales";
import "@blocknote/shadcn/style.css";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { mdiContentSave } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useCreatePage, useUpdatePage } from "../hooks/usePages";
import { useTheme } from "../contexts/ThemeContext";
import type { components } from "../api/schema";

type TiptapDocument = components["schemas"]["TiptapDocument"];

interface PageEditorProps {
  entityType: string;
  entityUid: string;
  initialContent?: TiptapDocument;
  onSave?: (content: TiptapDocument) => void;
  showFloatingSaveButton?: boolean;
  onChangeDetected?: (hasChanges: boolean) => void;
  isExistingPage?: boolean;
  onSaved?: () => void; // Called when save is successful
  /**
   * When given, the editor hands the content to the caller instead of saving
   * the asset's single page (knowledge notes, spec 2026-10-10-06). The caller
   * owns the persistence; a rejected promise keeps the editor dirty.
   */
  onSubmit?: (payload: { content: TiptapDocument; text_content: string }) => Promise<void>;
  /** Draw a Save button under the editor (submit mode). */
  showInlineSaveButton?: boolean;
  /** Prefix of the data-testid of the inline Save button. */
  testIdPrefix?: string;
}

export function PageEditor({
  entityType,
  entityUid,
  initialContent,
  onSave,
  showFloatingSaveButton = true,
  onChangeDetected,
  isExistingPage,
  onSaved,
  onSubmit,
  showInlineSaveButton = false,
  testIdPrefix = "page",
}: PageEditorProps) {
  const [hasChanges, setHasChanges] = useState(false);
  const { actualTheme } = useTheme();
  const { t, i18n } = useTranslation("common");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateHasChanges = (changes: boolean) => {
    setHasChanges(changes);
    onChangeDetected?.(changes);
  };

  const createPageMutation = useCreatePage();
  const updatePageMutation = useUpdatePage();

  // Use explicit prop if provided, otherwise fall back to checking initialContent
  const pageExists = isExistingPage !== undefined ? isExistingPage : !!initialContent;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSaving = createPageMutation.isPending || updatePageMutation.isPending || isSubmitting;

  // Convert Tiptap JSON to BlockNote blocks if needed
  const convertTiptapToBlockNote = (tiptapDoc?: TiptapDocument) => {
    if (!tiptapDoc?.content || !Array.isArray(tiptapDoc.content)) return undefined;

    // For now, start with simple conversion - BlockNote will handle basic structures
    try {
      // Convert Tiptap nodes to BlockNote blocks
      const blocks = tiptapDoc.content.map((node: any, index: number) => {
        if (node.type === "paragraph") {
          return {
            id: `block-${index}`,
            type: "paragraph",
            content: node.content?.[0]?.text || "",
          };
        }
        if (node.type === "heading") {
          return {
            id: `block-${index}`,
            type: "heading",
            props: {
              level: node.attrs?.level || 1,
            },
            content: node.content?.[0]?.text || "",
          };
        }
        // Default to paragraph for unknown types
        return {
          id: `block-${index}`,
          type: "paragraph",
          content: node.content?.[0]?.text || "",
        };
      });

      return blocks.length > 0 ? blocks : undefined;
    } catch (error) {
      console.warn("Error converting Tiptap to BlockNote:", error);
      return undefined;
    }
  };

  const editor = useCreateBlockNote({
    initialContent: convertTiptapToBlockNote(initialContent) as any,
    // Follow the UI locale (placeholder "Enter text or type '/'…", slash
    // menu, etc.) instead of BlockNote's English default.
    dictionary: (blockNoteLocales as any)[i18n.language] ?? blockNoteLocales.en,
  });

  useEffect(() => {
    if (!editor) return;

    const handleChange = () => {
      updateHasChanges(true);
    };

    // Listen for content changes
    editor.onEditorContentChange(handleChange);

    return () => {
      // Cleanup if needed
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Update editor content when initialContent changes (fallback for content updates)
  useEffect(() => {
    if (!editor) return;
    
    const newContent = convertTiptapToBlockNote(initialContent);
    
    // Simple check: if we have new content and editor is currently empty, populate it
    const currentBlocks = editor.document;
    const isEditorEmpty = !currentBlocks || currentBlocks.length === 0 || 
      (currentBlocks.length === 1 && currentBlocks[0].type === "paragraph" && !currentBlocks[0].content);
    
    if (newContent && newContent.length > 0 && isEditorEmpty) {
      // Only populate empty editor to avoid overwriting user changes
      editor.replaceBlocks(currentBlocks, newContent as any);
      updateHasChanges(false);
    }
  }, [editor, initialContent, updateHasChanges]);

  const handleSave = useCallback(async () => {
    if (!editor) return;

    // Convert BlockNote document to Tiptap format for backend compatibility
    const blocks = editor.document;

    const convertBlockNoteToTiptap = (blocks: any[]): any[] => {
      return blocks.map((block: any) => {
        switch (block.type) {
          case "paragraph":
            return {
              type: "paragraph",
              content: block.content
                ? [{ type: "text", text: block.content }]
                : []
            };
          case "heading":
            return {
              type: "heading",
              attrs: { level: block.props?.level || 1 },
              content: block.content
                ? [{ type: "text", text: block.content }]
                : []
            };
          default:
            // Convert unknown blocks to paragraphs
            return {
              type: "paragraph",
              content: block.content
                ? [{ type: "text", text: block.content }]
                : []
            };
        }
      });
    };

    const content: TiptapDocument = {
      type: "doc",
      content: blocks ? convertBlockNoteToTiptap(blocks) : []
    };

    // Export markdown content for text_content
    const markdownContent = await editor.blocksToMarkdownLossy(blocks || []);

    try {
      if (onSubmit) {
        setIsSubmitting(true);
        try {
          await onSubmit({ content, text_content: markdownContent });
        } finally {
          setIsSubmitting(false);
        }
      } else if (pageExists) {
        await updatePageMutation.mutateAsync({
          entityType,
          entityUid,
          title: undefined,
          content,
          text_content: markdownContent,
        });
      } else {
        await createPageMutation.mutateAsync({
          entityType,
          entityUid,
          title: undefined,
          content,
          text_content: markdownContent,
        });
      }

      updateHasChanges(false);
      onSave?.(content);
      onSaved?.(); // Call the onSaved callback for external save buttons
    } catch (error) {
      console.error("Failed to save page:", error);
      // TODO: Show error toast
    }
  }, [editor, entityType, entityUid, pageExists, createPageMutation, updatePageMutation, updateHasChanges, onSave, onSaved, onSubmit])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Ctrl+S globally for save functionality
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        // Check if we're in the editor area
        const target = e.target as Element;
        const editorContainer = target.closest('[data-editor]');

        if (editorContainer) {
          e.preventDefault();
          e.stopPropagation();
          handleSave();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleSave]);

  if (!editor) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t("editor.loading")}</div>
      </div>
    );
  }

  const handleEditorKeyDown = (e: React.KeyboardEvent) => {
    // Prevent editor shortcuts from bubbling up to global handlers
    if ((e.metaKey || e.ctrlKey) && ['b', 'i', 'u', 'z', 'y'].includes(e.key.toLowerCase())) {
      e.stopPropagation();
    }
  };

  return (
    <div
      key={actualTheme} // Force remount when theme changes
      className="relative"
      data-editor="true"
      onKeyDown={handleEditorKeyDown}
    >
      {/* Floating save button */}
      {showFloatingSaveButton && hasChanges && (
        <div className="fixed top-4 right-4 z-50">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            className="shadow-lg"
          >
            <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
            {isSaving ? t("editor.saving") : t("actions.save")}
          </Button>
        </div>
      )}


      {/* BlockNote Editor */}
      <div className="min-h-96 prose prose-lg dark:prose-invert max-w-none prose-h1:text-[2.16em] prose-h2:text-[1.8em]" data-theme={actualTheme}>
        <BlockNoteView
          key={actualTheme} // Force remount when theme changes
          editor={editor}
          theme={['dark', 'midnight', 'halloween'].includes(actualTheme) ? 'dark' : 'light'}
        />
      </div>

      {showInlineSaveButton && (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            data-testid={`${testIdPrefix}-save-button`}
          >
            <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
            {isSaving ? t("editor.saving") : t("actions.save")}
          </Button>
        </div>
      )}

      {/* Optional status indicator */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-sm text-muted-foreground">
        <div>
          {hasChanges ? t("editor.unsaved_changes") : t("editor.saved")}
        </div>
        <div>
          {t("editor.save_shortcut_hint")}
        </div>
      </div>
    </div>
  );
}
